import { apiService } from './api';

export interface AnswerRatingAggregate {
  value: number | null;
  count: number;
}

export interface PlatformImpact {
  totalAnalyses: number;
  estimatedSavedHours: number;
  answerRating: AnswerRatingAggregate;
}

declare global {
  interface Window {
    __PALEN_PLATFORM_IMPACT__?: unknown;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return isNonNegativeNumber(value) && Number.isInteger(value);
}

export function parsePlatformImpact(value: unknown): PlatformImpact | null {
  if (
    !isRecord(value)
    || !isNonNegativeInteger(value.totalAnalyses)
    || !isNonNegativeNumber(value.estimatedSavedHours)
  ) {
    return null;
  }

  if (value.answerRating === undefined) {
    return {
      totalAnalyses: value.totalAnalyses,
      estimatedSavedHours: value.estimatedSavedHours,
      answerRating: { value: null, count: 0 },
    };
  }

  if (!isRecord(value.answerRating)) return null;

  const ratingValue = value.answerRating.value;
  const ratingCount = value.answerRating.count;
  const hasNoRating = ratingValue === null && ratingCount === 0;
  const hasRating = typeof ratingValue === 'number'
    && Number.isFinite(ratingValue)
    && ratingValue >= 1
    && ratingValue <= 5
    && isNonNegativeInteger(ratingCount)
    && ratingCount > 0;

  if (
    !hasNoRating && !hasRating
  ) {
    return null;
  }

  return {
    totalAnalyses: value.totalAnalyses,
    estimatedSavedHours: value.estimatedSavedHours,
    answerRating: {
      value: ratingValue,
      count: ratingCount,
    },
  };
}

export function requirePlatformImpact(value: unknown): PlatformImpact {
  const impact = parsePlatformImpact(value);
  if (!impact) {
    throw new TypeError('The public platform impact response is invalid.');
  }
  return impact;
}

export async function fetchPlatformImpact(): Promise<PlatformImpact> {
  return requirePlatformImpact(
    await apiService.get<unknown>('/public/platform-impact', undefined, true),
  );
}

export function readPrerenderedPlatformImpact(): PlatformImpact | undefined {
  if (typeof window === 'undefined') return undefined;
  return parsePlatformImpact(window.__PALEN_PLATFORM_IMPACT__) || undefined;
}
